import { useMenuStore } from "@/store";
import { storeToRefs } from "pinia";
import { useI18n } from 'vue-i18n';
import { ref, watch, onMounted } from "vue";
import { useRoute } from "vue-router";

export function useLanguage() {
  interface ModalLangModel {
    label: string,
    value: string,
    icon: string,
    image: string,
  };
  type LanguageInfo = {
    label: string;
    icon: string;
    image: string;
  };

  const LANGUAGES_INFO: {
    [key: string]: LanguageInfo;
    EN: LanguageInfo;
    PT: LanguageInfo;
    ES: LanguageInfo;
  } = {
    EN: { label: 'ENGLISH', icon: 'usa', image: 'en' },
    PT: { label: 'PORTUGUESE', icon: 'bra', image: 'pt' },
    ES: { label: 'SPANISH', icon: 'esp', image: 'es' },
  };

  const menuStore = useMenuStore();
  const { customerData, defaultLanguageLocalStorage, trakingData, currentSupplier } = storeToRefs(menuStore);
  const route = useRoute();
  const { t, te } = useI18n();
  const { n } = useI18n();
  const languages = ref<ModalLangModel[]>([]);
  const changeLang = ref<string>("");

  const getLabels = (tag: string) => {
    if (tag) {
      const tagDefault = `${tag}.DEFAULT`;
      // Si country_code no está disponible, usar DEFAULT directamente
      if (!customerData.value?.country_code) {
        return t(tagDefault);
      }
      const tagCountry = `${tag}.${customerData.value.country_code}`;
      const labelsDefault: any = t(tagDefault);
      if (!te(tagCountry)) return labelsDefault;
      return t(tagCountry);
    };
  };

  const parseFractionDigits = (value: unknown): number | undefined => {
    if (value === null || value === undefined) return undefined;

    let parsed: number;

    if (typeof value === 'number') {
      parsed = value;
    } else if (typeof value === 'string') {
      const normalized = value.trim().replace(',', '.');
      if (!normalized || ['null', 'undefined', 'nan'].includes(normalized.toLowerCase())) {
        return undefined;
      }
      parsed = Number(normalized);
    } else {
      return undefined;
    }

    if (!Number.isFinite(parsed)) return undefined;

    const normalizedDigits = Math.trunc(parsed);
    if (normalizedDigits < 0) return 0;
    if (normalizedDigits > 6) return 6;
    return normalizedDigits;
  };

  const HIDE_CURRENCY_SYMBOL_SUPPLIER_IDS = [21588];

  const shouldHideCurrencySymbol = () => {
    const supplierCandidates = [
      Number(trakingData.value?.supplier_id),
      Number(currentSupplier.value?.id),
      Number(route.params?.supplier_id),
    ];

    if (supplierCandidates.some((id) => HIDE_CURRENCY_SYMBOL_SUPPLIER_IDS.includes(id))) {
      return true;
    }

    return Number((customerData.value as any)?.id) === 21588;
  };

  const priceCurrencyLocale = (data: any, decimals?: number) => {
    if (!customerData.value?.currency_locale && !customerData.value?.currency) return data;

    let fractionDigits = parseFractionDigits(decimals);

    if (fractionDigits === undefined) {
      const decimalCandidates: unknown[] = [
        customerData.value?.new_menu1_params?.decimal_places,
        customerData.value?.new_menu1_params?.decimals,
        (customerData.value as any)?.decimal_places,
        (customerData.value as any)?.decimals,
      ];

      for (const candidate of decimalCandidates) {
        const parsedCandidate = parseFractionDigits(candidate);
        if (parsedCandidate !== undefined) {
          fractionDigits = parsedCandidate;
          break;
        }
      }

      if (fractionDigits === undefined) {
        if (['COP', 'CLP', 'CRC', 'PYG', 'VES'].includes(customerData.value?.currency || '')) {
          fractionDigits = 0;
        } else {
          fractionDigits = 2;
        }
      }
    }
    
    try {
      const numValue = Number(data);
      if (isNaN(numValue)) return data;
      
      const formatter = new Intl.NumberFormat(
        customerData.value?.currency_locale || 'es-CO',
        shouldHideCurrencySymbol()
          ? {
              style: 'decimal',
              minimumFractionDigits: fractionDigits,
              maximumFractionDigits: fractionDigits
            }
          : {
              style: 'currency',
              currency: customerData.value?.currency || 'COP',
              minimumFractionDigits: fractionDigits,
              maximumFractionDigits: fractionDigits
            }
      );
      
      return formatter.format(numValue);
    } catch (error) {
      console.error('Price formatting error:', error);
      return data;
    }
  };
  const languagesInstance = () => {
    if (!customerData.value?.langs || !customerData.value?.language) return;
    const allLanguages = [...(customerData.value.langs || []), customerData.value.language].filter(value => Object.keys(LANGUAGES_INFO).includes(value));
    languages.value = [...new Set(allLanguages)].map(item => {
      const { label, icon, image } = LANGUAGES_INFO[item];
      return {
        label: getLabels(`LANGUAGE.RADIOLABEL${label}`),
        value: item,
        icon: `modal-flag modal-flag__${icon}`,
        image: `menuLat-flag-image__${image}`
      };
    });
    if (allLanguages.includes(defaultLanguageLocalStorage.value)) {
      changeLang.value = defaultLanguageLocalStorage.value;
    }
  };
  const getLabelsWithVariables = (tag:string,keyVar:string,variable:string) => {
    if (tag) {
      const tagDefault = `${tag}.DEFAULT`;
      const labelsDefault: string = t(tagDefault,{ keyVar: variable });
      return labelsDefault;
    };
  };

  watch(customerData, languagesInstance);
  onMounted(languagesInstance);

  return {
    getLabels, priceCurrencyLocale, languages,
    changeLang, languagesInstance, customerData,
    getLabelsWithVariables
  };
}
